import { useState } from 'react';
import { toast } from 'sonner';

type EmailSaveErrorToastProps = {
  toastId: string | number;
  detail: string;
  onUndo: () => void;
  onReset: () => void;
};

function isHandlebarsTemplateError(message: string): boolean {
  return /handlebars|parse error|endblock|\{\{#/i.test(message);
}

function EmailSaveErrorToast({
  toastId,
  detail,
  onUndo,
  onReset,
}: EmailSaveErrorToastProps) {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className='w-[22rem] rounded-lg border border-red-200 bg-white p-3 text-sm shadow-lg'>
      <p className='font-semibold text-red-700'>Couldn&apos;t save this email</p>
      <p className='mt-1 text-gray-700'>
        Some template tags are incomplete. For example, a {'{{#each}}'} is
        missing its closing {'{{/each}}'}.
      </p>
      <p className='mt-1 text-gray-600'>
        Undo your last change, or reset to default to start over.
      </p>
      <div className='mt-3 flex flex-wrap items-center gap-2'>
        <button
          type='button'
          className='rounded-md bg-gray-900 px-2.5 py-1 text-xs font-medium text-white'
          onClick={() => {
            onUndo();
            toast.dismiss(toastId);
          }}
        >
          Undo
        </button>
        <button
          type='button'
          className='rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-800'
          onClick={() => {
            onReset();
            toast.dismiss(toastId);
          }}
        >
          Reset to default
        </button>
        <button
          type='button'
          className='px-1 py-1 text-xs text-gray-600 underline'
          onClick={() => {
            setShowDetails((open) => !open);
          }}
        >
          {showDetails ? 'Hide details' : 'Show details'}
        </button>
      </div>
      {showDetails ? (
        <pre className='mt-2 max-h-28 overflow-auto whitespace-pre-wrap text-xs text-gray-500'>
          {detail}
        </pre>
      ) : null}
    </div>
  );
}

export function showEmailTemplateSaveError(options: {
  detail: string;
  onUndo: () => void;
  onReset: () => void;
}): void {
  if (!isHandlebarsTemplateError(options.detail)) {
    toast.error(options.detail, { duration: Infinity });
    return;
  }

  toast.custom(
    (toastId) => (
      <EmailSaveErrorToast
        toastId={toastId}
        detail={options.detail}
        onUndo={options.onUndo}
        onReset={options.onReset}
      />
    ),
    { duration: Infinity },
  );
}
